import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Share2,
  Users,
  UserPlus,
  X,
  Loader2,
  Check,
  Mail
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * ShareDialog - Wiederverwendbarer Dialog zum Teilen von Inhalten
 * 
 * Props:
 * - open: boolean - Ob der Dialog geöffnet ist
 * - onOpenChange: (open: boolean) => void - Callback beim Öffnen/Schließen
 * - itemId: string - ID des zu teilenden Elements
 * - itemType: string - Typ des Elements (jobs, styles, persons, logos, analyses, meta-styles)
 * - itemName: string - Name des Elements für die Anzeige
 * - sharedWith: string[] - Array von User-IDs, mit denen bereits geteilt wird
 * - onShareUpdate: () => void - Callback nach erfolgreichem Teilen
 */
export const ShareDialog = ({
  open,
  onOpenChange,
  itemId,
  itemType,
  itemName,
  sharedWith = [],
  onShareUpdate
}) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [sharing, setSharing] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);

  // Fetch users for sharing
  useEffect(() => {
    if (open) {
      fetchUsers();
    }
  }, [open]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const response = await axios.get(`${API}/users/list`);
      setUsers(response.data.users || []);
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error("Fehler beim Laden der Benutzer");
    } finally {
      setLoadingUsers(false);
    }
  };

  // Get endpoint based on item type
  const getShareEndpoint = () => {
    return `${API}/${itemType}/${itemId}/share`;
  };

  const handleShare = async (userEmail) => {
    if (!userEmail) return;
    
    setSharing(true);
    try {
      await axios.post(getShareEndpoint(), { email: userEmail });
      toast.success(`Mit ${userEmail} geteilt`);
      setSelectedUser(null);
      setSearchQuery("");
      if (onShareUpdate) onShareUpdate();
    } catch (err) {
      console.error("Error sharing:", err);
      const message = err.response?.data?.detail || "Fehler beim Teilen";
      toast.error(message);
    } finally {
      setSharing(false);
    }
  };

  const handleUnshare = async (userId) => {
    try {
      await axios.delete(`${API}/${itemType}/${itemId}/share/${userId}`);
      toast.success("Teilen aufgehoben");
      if (onShareUpdate) onShareUpdate();
    } catch (err) {
      console.error("Error unsharing:", err);
      toast.error("Fehler beim Aufheben des Teilens");
    }
  };

  // Filter users not already shared with
  const availableUsers = users.filter(u => !sharedWith.includes(u.id));
  const sharedUsers = users.filter(u => sharedWith.includes(u.id));
  
  // Filter by search query
  const filteredUsers = availableUsers.filter(u => 
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5 text-primary" />
            Teilen
          </DialogTitle>
          <DialogDescription>
            "{itemName}" mit anderen Benutzern teilen
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* User Search */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Benutzer hinzufügen</Label>
            <div className="relative">
              <Command className="rounded-lg border shadow-md">
                <CommandInput 
                  placeholder="E-Mail oder Name suchen..."
                  value={searchQuery}
                  onValueChange={setSearchQuery}
                  data-testid="share-user-search"
                />
                <CommandList>
                  {loadingUsers ? (
                    <div className="p-4 space-y-2">
                      <Skeleton className="h-8 w-full" />
                      <Skeleton className="h-8 w-full" />
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>Kein Benutzer gefunden</CommandEmpty>
                      <CommandGroup heading="Verfügbare Benutzer">
                        {filteredUsers.slice(0, 5).map((user) => (
                          <CommandItem
                            key={user.id}
                            value={user.email}
                            onSelect={() => handleShare(user.email)}
                            className="cursor-pointer"
                            data-testid={`share-user-${user.id}`}
                          >
                            <div className="flex items-center gap-2 w-full">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="text-xs font-medium text-primary">
                                  {user.name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium truncate">{user.name || "Unbenannt"}</p>
                                <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                              </div>
                              {sharing ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <UserPlus className="h-4 w-4 text-muted-foreground" />
                              )}
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </div>
          </div>

          {/* Currently Shared With */}
          {sharedUsers.length > 0 && (
            <div className="space-y-2">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Geteilt mit ({sharedUsers.length})
              </Label>
              <ScrollArea className="h-[150px] rounded-md border p-2">
                <div className="space-y-2">
                  {sharedUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-2 rounded-md bg-muted/50 hover:bg-muted transition-colors"
                      data-testid={`shared-user-${user.id}`}
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-medium text-primary">
                            {user.name?.charAt(0)?.toUpperCase() || user.email.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{user.name || "Unbenannt"}</p>
                          <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnshare(user.id)}
                        className="flex-shrink-0 h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        data-testid={`unshare-user-${user.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Empty State */}
          {sharedUsers.length === 0 && (
            <div className="text-center py-4 text-muted-foreground">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">Noch mit niemandem geteilt</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Schließen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * ShareButton - Einfacher Share Button mit Tooltip
 */
export const ShareButton = ({ onClick, sharedCount = 0, disabled = false, size = "sm" }) => {
  return (
    <Button
      variant="ghost"
      size={size}
      onClick={onClick}
      disabled={disabled}
      className="gap-1"
      data-testid="share-button"
    >
      <Share2 className="h-4 w-4" />
      {sharedCount > 0 && (
        <Badge variant="secondary" className="h-5 px-1.5 text-xs">
          {sharedCount}
        </Badge>
      )}
    </Button>
  );
};

export default ShareDialog;
